<template>
  <div
    class="gap-4 pb-4 flex flex-1 flex-col items-center"
    @click="$emit('click')"
  >
    <div
      v-if="ui.isTextWrapper"
      id="exercises-text-wrapper"
      ref="exercises-text-wrapper"
      class="w-full flex flex-1 opacity-0"
    >
      <ExercisesContentText
        :key="exercisesTextWrapperKey"
        :text="activeExercise.text"
        :style="{
          height: `${exercisesTextWrapper.height}px`,
          width: `${exercisesTextWrapper.width}px`
        }"
      />
    </div>

    <div
      id="exercises-buttons-wrapper"
      ref="exercises-buttons-wrapper"
      class="w-full px-4 gap-4 grid grid-cols-6 flex-initial"
    >
      <BaseButton
        v-for="(answer, index) of possibleAnswers"
        :key="`${activeExercise.text}-${answer}`"
        :ref="`exercises-buttons-${index}`"
        :class="{
          'col-span-6': possibleAnswers.length <= 3,
          'col-span-3': possibleAnswers.length <= 6,
          'col-span-2': possibleAnswers.length > 6
        }"
        color="light"
        @click="() => handleAnswer(answer, index)"
      >
        <span>{{ answer }}</span>
      </BaseButton>
    </div>
  </div>
</template>

<script>
import * as motion from 'motion'
import { mapState, mapGetters, mapMutations } from 'vuex'

import BaseButton from '@/components/Base/BaseButton.vue'
import ExercisesContentText from '@/components/Hub/Exercises/ExercisesContentText.vue'

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

export default {
  name: 'HubExercisesContent',

  components: { BaseButton, ExercisesContentText },

  data: () => ({
    resizeObserver: null,
    exercisesTextWrapper: { height: 0, width: 0 },

    ui: {
      isTextWrapper: false
    }
  }),

  computed: {
    ...mapState('exercises', ['exercises']),
    ...mapGetters('exercises', [
      'activeExercise',
      'defineIsCorrect',
      'isFinished'
    ]),

    possibleAnswers() {
      return this.activeExercise.possibleAnswers[0]
    },

    exercisesTextWrapperKey() {
      return this.exercisesTextWrapper.height + this.exercisesTextWrapper.width
    }
  },

  mounted() {
    this.renderNextExercise()
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },

  methods: {
    ...mapMutations('exercises', {
      setActiveIndex: 'SET_ACTIVE_INDEX',
      addToHistory: 'ADD_TO_HISTORY'
    }),

    async handleAnswer(answer, index) {
      const isCorrect = this.defineIsCorrect({
        exercise: this.activeExercise,
        answer
      })

      const button = this.$refs[`exercises-buttons-${index}`][0]

      this.$gtag.event('exercise_answer', { isCorrect })

      if (isCorrect) {
        button.triggerCorrectAnimation()
        this.$gtag.event('exercise_answer_true')
      } else {
        button.triggerIncorrectAnimation()
        this.$gtag.event('exercise_answer_false')
      }

      await wait(150)

      const historyItem = {
        exercise: structuredClone(this.activeExercise),
        answer,
        isCorrect,
        explanation: null
      }
      this.addToHistory(historyItem)

      // if (this.isFinished) {
      //   this.$emit('show-results')
      //   return
      // }

      if (isCorrect) {
        // this.setActiveIndex(this.exercises.activeIndex + 1)
        // this.renderNextExercise()
        this.$emit('correct-answer')
      } else {
        this.$emit('wrong-answer')
      }
    },

    renderNextExercise() {
      this.exercisesTextWrapper = { height: 0, width: 0 }

      const renderText = () => {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect()
        }

        this.resizeObserver = new ResizeObserver(entries => {
          entries.forEach(() => {
            const wrapper = this.$refs['exercises-text-wrapper']

            this.exercisesTextWrapper = {
              height: wrapper?.clientHeight,
              width: wrapper?.clientWidth
            }
          })
        })

        this.resizeObserver.observe(this.$refs['exercises-text-wrapper'])
      }

      const renderButtons = async () => {
        this.ui.isTextWrapper = true

        await this.$nextTick()

        motion
          .animate(
            this.$refs['exercises-text-wrapper'],
            { opacity: 1 },
            { duration: 0 }
          )
          .finished.then(() => {
            this.$nextTick(renderText)
          })
      }

      const init = () => {
        this.ui.isTextWrapper = false

        this.$nextTick(renderButtons)
      }

      if (!this.$refs['exercises-text-wrapper']) {
        init()
        return
      }

      motion
        .animate(
          this.$refs['exercises-text-wrapper'],
          { opacity: 0 },
          { duration: 0 }
        )
        .finished.then(init)
    }
  }
}
</script>
